/** Website CMS media library: upload images and video (raw body), alt text EN/AR, filter by kind, delete (Super Admin). */
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { CMS_MEDIA_TYPES, type CmsMediaView } from '@qm/contracts/cms';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Field, Loadable, Notice, Screen, Tabs, When } from '../kit/ui';
import { canDeleteMedia, canEditCms, canViewCms, currentWebsiteOrigin, megabytes, validateUpload } from '../lib/cms-logic';
import { useAdminApi } from './layout';
import { ClientError, CmsError, ConfirmButton, MediaThumb } from './cms-shared';

const KINDS = ['all', 'image', 'video'] as const;
type KindFilter = (typeof KINDS)[number];

export function CmsMediaPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const [sp, setSp] = useSearchParams();
  const kind: KindFilter = (KINDS as readonly string[]).includes(sp.get('kind') ?? '') ? (sp.get('kind') as KindFilter) : 'all';
  const list = useResource(`cms:media:${kind === 'all' ? '' : kind}`, () => api.cmsMedia(kind === 'all' ? undefined : kind));
  if (!canViewCms(roles)) return <Screen title={t('portal.admin.cms.media.title')}><Notice testId="cms-no-access">{t('portal.admin.cms.noAccess')}</Notice></Screen>;
  const origin = currentWebsiteOrigin();
  return (
    <Screen title={t('portal.admin.cms.media.title')} subtitle={t('portal.admin.cms.media.subtitle')} testId="cms-media">
      {canEditCms(roles) ? <UploadForm onUploaded={list.reload} /> : <Notice testId="cms-read-only">{t('portal.admin.cms.readOnly')}</Notice>}
      <Tabs label={t('portal.admin.cms.media.filter')} active={kind} onSelect={(v) => setSp(v === 'all' ? {} : { kind: v })} tabs={KINDS.map((k) => ({ id: k, label: t(`portal.admin.cms.media.kind.${k}`) }))} />
      <Loadable resource={list}>
        {(rows) => (rows.length === 0 ? <Text tone="muted">{t('portal.admin.cms.media.none')}</Text> : (
          <div className="qx-media-grid" data-testid="media-grid">
            {rows.map((m) => <MediaCard key={m.id} media={m} origin={origin} onChanged={list.reload} />)}
          </div>
        ))}
      </Loadable>
    </Screen>
  );
}

export function UploadForm({ onUploaded }: { onUploaded: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [file, setFile] = useState<File | null>(null);
  const [altEn, setAltEn] = useState('');
  const [altAr, setAltAr] = useState('');
  const [check, setCheck] = useState<{ reason: string; mb?: string } | null>(null);
  const upload = useAction(api.cmsUpload);
  const pick = (f: File | null) => {
    setFile(f);
    if (!f) { setCheck(null); return; }
    const r = validateUpload(f);
    setCheck(r.ok ? null : { reason: r.reason, ...(r.maxBytes ? { mb: String(megabytes(r.maxBytes)) } : {}) });
  };
  return (
    <Panel data-testid="media-upload">
      <Heading level={2}>{t('portal.admin.cms.media.upload')}</Heading>
      <Text tone="muted">{t('portal.admin.cms.media.limits')}</Text>
      <form className="qx-toolbar" onSubmit={async (e) => {
        e.preventDefault();
        if (!file || check) return;
        const r = await upload.run(file, { altEn: altEn.trim(), altAr: altAr.trim() });
        if (r) { setFile(null); setAltEn(''); setAltAr(''); onUploaded(); }
      }}>
        <Field label={t('portal.admin.cms.media.file')}>{(id) => <input id={id} type="file" name="file" accept={CMS_MEDIA_TYPES.join(',')} onChange={(e) => pick(e.target.files?.[0] ?? null)} />}</Field>
        <Field label={t('portal.admin.cms.media.altEn')}>{(id) => <input id={id} name="altEn" value={altEn} maxLength={300} onChange={(e) => setAltEn(e.target.value)} />}</Field>
        <Field label={t('portal.admin.cms.media.altAr')}>{(id) => <input id={id} name="altAr" dir="rtl" lang="ar" value={altAr} maxLength={300} onChange={(e) => setAltAr(e.target.value)} />}</Field>
        <Button type="submit" variant="primary" disabled={!file || !!check || upload.pending} data-testid="upload-submit">{t('portal.admin.cms.media.uploadSubmit')}</Button>
      </form>
      {check ? <ClientError reason={check.reason} params={check.mb ? { mb: check.mb } : undefined} testId="upload-check" /> : null}
      <CmsError error={upload.error} />
    </Panel>
  );
}

export function MediaCard({ media, origin, onChanged }: { media: CmsMediaView; origin: string; onChanged: () => void }) {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const editable = canEditCms(roles);
  const [altEn, setAltEn] = useState(media.altEn);
  const [altAr, setAltAr] = useState(media.altAr);
  const save = useAction(() => api.cmsUpdateMedia(media.id, altEn.trim(), altAr.trim()));
  const del = useAction(() => api.cmsDeleteMedia(media.id));
  const changed = altEn !== media.altEn || altAr !== media.altAr;
  return (
    <article className="qx-media" data-testid="media-card" data-media={media.id} data-kind={media.kind}>
      <MediaThumb media={media} id={media.id} origin={origin} />
      <div className="qx-media__meta">
        <span className="qx-media__name">{media.filename}</span>
        <span className="qx-toolbar"><Badge>{t(`portal.admin.cms.media.kind.${media.kind}`)}</Badge> <Text tone="muted">{t('portal.admin.cms.media.size', { mb: megabytes(media.bytes) })}</Text></span>
        <Text tone="muted"><code>{media.id}</code> · <When iso={media.createdAt} /></Text>
      </div>
      <Field label={t('portal.admin.cms.media.altEn')}>{(id) => <input id={id} value={altEn} maxLength={300} disabled={!editable} onChange={(e) => setAltEn(e.target.value)} />}</Field>
      <Field label={t('portal.admin.cms.media.altAr')}>{(id) => <input id={id} dir="rtl" lang="ar" value={altAr} maxLength={300} disabled={!editable} onChange={(e) => setAltAr(e.target.value)} />}</Field>
      <span className="qx-toolbar">
        {editable ? <Button onClick={async () => { if (await save.run()) onChanged(); }} disabled={!changed || save.pending} data-testid="alt-save">{t('portal.admin.save')}</Button> : null}
        {canDeleteMedia(roles) ? (
          <ConfirmButton testId="media-delete" label={t('portal.admin.cms.media.delete')} confirmLabel={t('portal.admin.cms.media.deleteConfirm')}
            question={t('portal.admin.cms.media.deleteQuestion')} onConfirm={async () => { if (await del.run()) onChanged(); }} disabled={del.pending} />
        ) : null}
      </span>
      <CmsError error={save.error ?? del.error} />
    </article>
  );
}
